import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Dialog, Button, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { TopUpWithdrawTxType } from 'src/type/topup-withdraw';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { fNumber } from 'src/utils/format-number';
import ImageWithPreview from 'src/components/ui-kit/image-with-preview';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { txAction } from './utils';

const Schema = Yup.object().shape({
  reason: Yup.string(),
});

const defaultValues = {
  reason: '',
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  topUpWithdrawTx: TopUpWithdrawTxType;
  handleReload: VoidFunction;
};

export default function TopupApproveDialog({
  open,
  onClose,
  topUpWithdrawTx,
  handleReload,
}: Props) {
  const currentDateTime = useCurrentDateTime();

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (topUpWithdrawTx.status !== TransactionStatus.PENDING) {
        throw new Error('ไม่สามารถอัพเดทข้อมูลได้');
      }

      if (topUpWithdrawTx.type !== TransactionType.TOPUP_CREDIT) {
        throw new Error('ไม่สามารถอัพเดทข้อมูลได้');
      }

      await axios.post(endpoints.transactions.approveTopup(topUpWithdrawTx.id), {
        note: data.reason,
        currentDateTime,
      });
      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const getInfo = () => {
    if (!topUpWithdrawTx) return [];

    const { user, creditAmount, adminBankAccount, paymentImageUrl } = topUpWithdrawTx;

    return [
      { label: 'ผู้ใช้งาน', value: `${user?.firstName} ${user?.lastName}` },
      {
        label: 'ยอดที่ต้องการเติม',
        value: `${fNumber(creditAmount || 0)} THB`,
      },
      {
        label: 'ชื่อธนาคาร (ระบบ)',
        value: adminBankAccount?.bank?.name || '-',
      },
      {
        label: 'ชื่อบัญชี (ระบบ)',
        value: adminBankAccount?.accountName || '-',
      },
      {
        label: 'เลขที่บัญชี (ระบบ)',
        value: adminBankAccount?.accountNumber || '-',
      },
      ...(paymentImageUrl
        ? [
            {
              label: 'หลักฐานการโอนเงิน',
              value: <ImageWithPreview src={paymentImageUrl} alt="" />,
            },
          ]
        : []),
    ];
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>อนุมัติรายการ{txAction(topUpWithdrawTx.type)}</DialogTitle>

        <DialogContent>
          <InfoGrid infos={getInfo()} />

          <RHFTextField
            multiline
            rows={3}
            name="reason"
            placeholder="หมายเหตุ"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            ดำเนินการต่อ
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
